import type { VehicleSectionV2 } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// @TODO: I copied the setup in Signers.schema.ts but is it okay that this is not using i18n for the validation messages?
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const ODOMETER_DISC_MILEAGE_LENGTH_MESSAGE = "Must be 7 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const NUMBERS_ONLY_MESSAGE = "Numbers only";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const LIC_PLATE_LENGTH_MESSAGE = "Must be 30 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const NON_NEGATIVE_MESSAGE = "Must be zero or greater";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const CPO_DATE_REQUIRED_MESSAGE = "This is a required field.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const CPO_DATE_FUTURE_MESSAGE = "Must not be a future date";

/**
 * Local-midnight-safe ISO `YYYY-MM-DD` for "today", mirroring dateInput.tsx's
 * `dateToIso` (not exported there, so duplicated here rather than reaching
 * into that module — same as Signers.schema.ts not sharing date logic with it).
 * @returns {string} Today's date as `YYYY-MM-DD`.
 */
function todayIso(): string {
  const now = new Date();
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// contractVehicleSchema — spike scope only: the fields that already use the
// dual-mode TextField/CurrencyField/DateField/CheckboxField (Odometer Disc
// Mileage, Lic Plate #, Total Selling Price, Certified Pre-Owned, CPO Date).
// Year/Make/Model/VIN/Color/Book Wholesale/Book Retail stay LabeledValue-driven,
// outside this schema.
// ---------------------------------------------------------------------------
export const ODOMETER_DISC_MILEAGE_MAX_LENGTH = 7;
export const LIC_PLATE_MAX_LENGTH = 30;
export const TOTAL_SELLING_PRICE_MAX_LENGTH = 9;

export const contractVehicleSchema = z
  .object({
    odometerDiscMileage: z
      .string()
      .max(ODOMETER_DISC_MILEAGE_MAX_LENGTH, ODOMETER_DISC_MILEAGE_LENGTH_MESSAGE)
      .regex(/^\d*$/, NUMBERS_ONLY_MESSAGE),
    licPlate: z.string().max(LIC_PLATE_MAX_LENGTH, LIC_PLATE_LENGTH_MESSAGE),
    // CurrencyField's onChange already emits `number | undefined` for an empty
    // input (see CurrencyInput's parseRawInput) — no emptyStringToUndefined
    // preprocessing needed here, unlike Signers' text-based number inputs.
    totalSellingPrice: z.number().nonnegative(NON_NEGATIVE_MESSAGE).optional(),
    // Never user-editable and never validated — just reflects whatever drives
    // CPO Date's visibility (see the superRefine below).
    isCertifiedPreOwned: z.boolean(),
    cpoDate: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.isCertifiedPreOwned) {
      return;
    }
    if (!data.cpoDate) {
      context.addIssue({ code: "custom", path: ["cpoDate"], message: CPO_DATE_REQUIRED_MESSAGE });
      return;
    }
    if (data.cpoDate > todayIso()) {
      context.addIssue({ code: "custom", path: ["cpoDate"], message: CPO_DATE_FUTURE_MESSAGE });
    }
    // @TODO: There's another validation we might want to add here for CPO Date but it hinges on data that doesn't exist yet
  });

export type ContractVehicleInput = z.infer<typeof contractVehicleSchema>;

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Trims a deal timestamp (e.g. `2023-06-15T00:00:00Z`) to its `YYYY-MM-DD` date part. Deliberately
 * string-based rather than `new Date()`, which parses as UTC and can shift the day.
 * @param {string | undefined} timestamp - Timestamp passed through from the deal.
 * @returns {string} The ISO date, or `""` when absent or not date-shaped.
 */
export function toIsoDate(timestamp?: string): string {
  return ISO_DATE_PREFIX.exec(timestamp ?? "")?.[0] ?? "";
}

/**
 * Whether the vehicle condition is NEW (case- and whitespace-insensitive). Anything else, including absent, is not.
 * @param {string | undefined} condition - `financial.vehicle.condition`.
 * @returns {boolean} True for a new vehicle.
 */
export function isNewVehicle(condition?: string): boolean {
  return condition?.trim().toUpperCase() === "NEW";
}

/**
 * Seeds the Contract Vehicle form from the V2 dashboard `vehicle` section. CPO Date is prefilled only
 * when the certification is verified (spec 5.3); the CPO row shows whenever the deal has CPO details.
 * @param {VehicleSectionV2} [vehicle] - The dashboard vehicle section.
 * @returns {ContractVehicleInput} Default form values.
 */
export function buildContractVehicleDefaultValues(vehicle?: VehicleSectionV2): ContractVehicleInput {
  const onDeal = vehicle?.vehicleOnDeal;
  const cpo = onDeal?.certifiedPreOwnedDetails;
  return {
    // `?? ""` / `?? undefined`: the BFF serializes VehicleOnDealV2's optionals as explicit JSON nulls,
    // which would otherwise stringify to "null" here and fail `z.number().optional()` below.
    odometerDiscMileage: String(onDeal?.odometerMileage ?? ""),
    licPlate: vehicle?.licensePlateNo ?? "",
    totalSellingPrice: onDeal?.msrp ?? undefined,
    isCertifiedPreOwned: !!cpo,
    cpoDate: cpo?.isVerified ? toIsoDate(cpo.certificationDate) : "",
  };
}
